import { useCallback, useEffect, useMemo, useState } from "react";
import type {
  TimeReportsV2Group,
  TimeReportsV2Item,
} from "services/api/company/timeReports.service";
import {
  ACTION_ORDER,
  resolveAvailableActions,
  resolveSignedTotalMinutes,
} from "../models/personView.actions";
import type { BulkAction } from "../models/personView.actions";
import type { PersonDetailSelection } from "../models/personView.types";
import { refreshOpenedDetail } from "./personSelection.logic";

interface UsePersonSelectionParams {
  rows: TimeReportsV2Group[];
  queryScopeKey: string;
}

interface UsePersonSelectionState {
  openedDetail: PersonDetailSelection | null;
  selectedDetails: PersonDetailSelection[];
  selectedDetailIdSet: Set<number>;
  isAllRowsSelected: boolean;
  isPartiallyRowsSelected: boolean;
  selectedCount: number;
  totalSelectedMinutes: number;
  availableBulkActions: BulkAction[];
  setOpenedDetail: (selection: PersonDetailSelection | null) => void;
  handleSelectionChange: (
    selections: PersonDetailSelection[],
    checked: boolean,
  ) => void;
  clearSelection: () => void;
  retainSelectionByIds: (ids: Set<number>) => void;
  toggleAllRowsSelection: (checked: boolean) => void;
}

const createLatestItemByIdMap = (
  rows: TimeReportsV2Group[],
): Map<number, TimeReportsV2Item> => {
  const next = new Map<number, TimeReportsV2Item>();
  rows.forEach((row) => {
    row.items.forEach((item) => {
      next.set(item.id, item);
    });
  });
  return next;
};

const createSelectionFromRowItem = (
  row: TimeReportsV2Group,
  item: TimeReportsV2Item,
): PersonDetailSelection => {
  return {
    detail: {
      id: item.id,
      dateLabel: "",
      weekdayLabel: "",
      totalTimeLabel: "",
      timeRows: [],
      statusTag: {
        text: "",
        iconName: "v2/document",
        tone: "info",
      },
      deviationTag: null,
    },
    item,
    workerName: row.name,
    workerRole: row.role,
    workerExternalId: row.externalId,
    workGroupName: item.workGroupName ?? null,
  };
};

export const usePersonSelection = (
  params: UsePersonSelectionParams,
): UsePersonSelectionState => {
  const [openedDetail, setOpenedDetail] =
    useState<PersonDetailSelection | null>(null);
  const [selectedDetailsById, setSelectedDetailsById] = useState<
    Map<number, PersonDetailSelection>
  >(new Map<number, PersonDetailSelection>());

  const latestItemById = useMemo(
    () => createLatestItemByIdMap(params.rows),
    [params.rows],
  );

  useEffect(() => {
    setSelectedDetailsById(new Map<number, PersonDetailSelection>());
    setOpenedDetail(null);
  }, [params.queryScopeKey]);

  useEffect(() => {
    setSelectedDetailsById((current) => {
      let changed = false;
      const next = new Map<number, PersonDetailSelection>();

      current.forEach((selection, id) => {
        const latestItem = latestItemById.get(id);
        if (!latestItem) {
          changed = true;
          return;
        }
        if (selection.item !== latestItem) {
          next.set(id, { ...selection, item: latestItem });
          changed = true;
          return;
        }
        next.set(id, selection);
      });

      return changed ? next : current;
    });

    setOpenedDetail((current) =>
      refreshOpenedDetail({
        openedDetail: current,
        latestItemsById: latestItemById,
      }),
    );
  }, [latestItemById]);

  const selectedDetails = useMemo<PersonDetailSelection[]>(
    () => Array.from(selectedDetailsById.values()),
    [selectedDetailsById],
  );
  const totalVisibleRowsCount = useMemo<number>(
    () => params.rows.reduce((count, row) => count + row.items.length, 0),
    [params.rows],
  );

  const selectedDetailIdSet = useMemo<Set<number>>(
    () => new Set(selectedDetailsById.keys()),
    [selectedDetailsById],
  );

  const selectedCount = selectedDetails.length;
  const selectedVisibleCount = useMemo<number>(
    () =>
      params.rows.reduce(
        (count, row) =>
          count +
          row.items.reduce(
            (rowCount, item) =>
              selectedDetailIdSet.has(item.id) ? rowCount + 1 : rowCount,
            0,
          ),
        0,
      ),
    [params.rows, selectedDetailIdSet],
  );
  const isAllRowsSelected =
    totalVisibleRowsCount > 0 && selectedVisibleCount === totalVisibleRowsCount;
  const isPartiallyRowsSelected =
    selectedVisibleCount > 0 && !isAllRowsSelected;

  const totalSelectedMinutes = useMemo<number>(
    () =>
      selectedDetails.reduce(
        (sum, selection) => sum + resolveSignedTotalMinutes(selection.item),
        0,
      ),
    [selectedDetails],
  );

  const availableBulkActions = useMemo<BulkAction[]>(() => {
    if (selectedDetails.length === 0) return [];

    const commonActions = new Set<BulkAction>(
      resolveAvailableActions(selectedDetails[0].item),
    );

    selectedDetails.slice(1).forEach((selection) => {
      const rowActions = resolveAvailableActions(selection.item);
      Array.from(commonActions).forEach((action) => {
        if (!rowActions.has(action)) {
          commonActions.delete(action);
        }
      });
    });

    return ACTION_ORDER.filter((action) => commonActions.has(action));
  }, [selectedDetails]);

  const handleSelectionChange = useCallback(
    (selections: PersonDetailSelection[], checked: boolean): void => {
      if (selections.length === 0) return;

      setSelectedDetailsById((current) => {
        const next = new Map(current);
        selections.forEach((selection) => {
          if (checked) {
            next.set(selection.item.id, selection);
            return;
          }
          next.delete(selection.item.id);
        });
        return next;
      });
    },
    [],
  );

  const clearSelection = useCallback((): void => {
    setSelectedDetailsById(new Map<number, PersonDetailSelection>());
  }, []);

  const retainSelectionByIds = useCallback((ids: Set<number>): void => {
    setSelectedDetailsById((current) => {
      let changed = false;
      const next = new Map<number, PersonDetailSelection>();

      current.forEach((selection, id) => {
        if (!ids.has(id)) {
          changed = true;
          return;
        }
        next.set(id, selection);
      });

      return changed ? next : current;
    });

    setOpenedDetail((current) => {
      if (!current) return current;
      if (ids.has(current.item.id)) return current;
      return null;
    });
  }, []);

  const toggleAllRowsSelection = useCallback(
    (checked: boolean): void => {
      if (totalVisibleRowsCount === 0) return;

      setSelectedDetailsById((current) => {
        const next = new Map(current);

        params.rows.forEach((row) => {
          row.items.forEach((item) => {
            if (checked) {
              next.set(item.id, createSelectionFromRowItem(row, item));
              return;
            }
            next.delete(item.id);
          });
        });

        return next;
      });
    },
    [params.rows, totalVisibleRowsCount],
  );

  return {
    openedDetail,
    selectedDetails,
    selectedDetailIdSet,
    isAllRowsSelected,
    isPartiallyRowsSelected,
    selectedCount,
    totalSelectedMinutes,
    availableBulkActions,
    setOpenedDetail,
    handleSelectionChange,
    clearSelection,
    retainSelectionByIds,
    toggleAllRowsSelection,
  };
};
